import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ArrowRight } from "lucide-react";
import {
  BookingWizardStep,
  ClinicDoctorListingItem,
  DoctorAppointmentSettingItem,
  PatientDetails,
  PatientFamilyDetails,
  SlotTimeItem,
} from "./types";
import { AppointmentData } from "../../services/api/types";
import { StepPatient } from "./StepPatient";
import { StepFindDoctor } from "./StepFindDoctor";
import { StepBookingDetails } from "./StepBookingDetails";
import { PaymentSummaryModal, PaymentFlowData } from "./PaymentSummaryModal";
import { AddNewPatientModal } from "../../shared/AddNewPatientModal";
import AddNewFamilyMember from "../../shared/AddNewFamilyMember";
import { PatientService } from "../../services/api/patient.service";
import { AppointmentService } from "../../services/api/appointment.service";
import { useAuth } from "../../context/AuthContext";
import { collection, doc, setDoc } from "firebase/firestore";
import { firestore as db } from "../../firebase/firebase";
import toast from "react-hot-toast";

interface BookingWizardProps {
  onClose: () => void;
  loadAppointments: () => void;
  loadAppointmentCount: () => void;
  mobileNumberProp?: string;
}

const STEPS: { key: BookingWizardStep; label: string; number: number }[] = [
  { key: "patient", label: "Patient", number: 1 },
  { key: "findDoctor", label: "Find Doctor", number: 2 },
  { key: "bookingDetails", label: "Booking Details", number: 3 },
];

export function BookingWizard({
  onClose,
  loadAppointments,
  loadAppointmentCount,
  mobileNumberProp,
}: BookingWizardProps) {
  const { user } = useAuth();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<BookingWizardStep>("patient");

  // Ref to track current step inside popstate handler (avoids stale closure)
  const currentStepRef = useRef<BookingWizardStep>(currentStep);
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  // --- Browser Back Button Interception ---
  // Pushes a dummy history entry so the browser back button is caught by popstate
  // instead of navigating away from the page. On back press:
  //   Step 3 → Step 2, Step 2 → Step 1, Step 1 → close wizard
  useEffect(() => {
    // Push initial dummy state
    window.history.pushState({ bookingWizard: true }, document.title, window.location.href);

    const handlePopState = () => {
      const step = currentStepRef.current;
      if (step === "bookingDetails") {
        // Go back to find doctor step
        setCurrentStep("findDoctor");
        // Push state again so next back press is also caught
        window.history.pushState({ bookingWizard: true }, document.title, window.location.href);
      } else if (step === "findDoctor") {
        // Go back to patient step
        setCurrentStep("patient");
        window.history.pushState({ bookingWizard: true }, document.title, window.location.href);
      } else {
        // On step 1, close the wizard
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [onClose]);

  // Patient state
  const [mobileNumber, setMobileNumber] = useState(mobileNumberProp || "");
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [patientDetails, setPatientDetails] = useState<PatientDetails | null>(
    null
  );
  const [previouslyConsultedDoctors, setPreviouslyConsultedDoctors] = useState<
    any[]
  >([]);
  const [previousFamilyDetails, setPreviousFamilyDetails] = useState<
    PatientFamilyDetails[]
  >([]);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<any>(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);

  // Doctor state
  const [selectedDoctor, setSelectedDoctor] =
    useState<ClinicDoctorListingItem | null>(null);
  const [selectedClinicSetting, setSelectedClinicSetting] =
    useState<DoctorAppointmentSettingItem | null>(null);

  // Booking state
  const [patientConcerns, setPatientConcerns] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<SlotTimeItem | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  // --- Step Navigation ---
  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);

  const handleBack = () => {
    if (currentStep === "findDoctor") setCurrentStep("patient");
    else if (currentStep === "bookingDetails") setCurrentStep("findDoctor");
  };

  const handleContinue = () => {
    if (currentStep === "patient") {
      if (!isVerified || !patientDetails) {
        toast.error("Please verify patient details first");
        return;
      }
      setCurrentStep("findDoctor");
    }
  };

  // --- Patient Handlers ---
  const handleVerify = async () => {
    if (!mobileNumber) {
      toast.error("Please enter a mobile number");
      return;
    }
    if (mobileNumber.length < 10) {
      toast.error("Invalid Mobile Number");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await PatientService.verifyPatientByMobileNumber(
        mobileNumber
      );
      if (response.success === "invalid") {
        toast.error("Something went wrong, please try again later");
        return;
      }

      if (response.success) {
        setIsVerified(true);
        setPatientDetails(response.data.patientDetails);
        setPreviouslyConsultedDoctors(
          response.data.previouslyConsultedDoctors || []
        );
        setPreviousFamilyDetails(
          response.data.patientFamilyDetails || []
        );
      } else {
        setShowNewPatientModal(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Verification failed");
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Doctor Handlers ---
  const handleDoctorSelected = (
    doctor: ClinicDoctorListingItem,
    setting: DoctorAppointmentSettingItem
  ) => {
    setSelectedDoctor(doctor);
    setSelectedClinicSetting(setting);
    // Reset slot selection
    setSelectedSlot(null);
    setSelectedDate(null);
    setCurrentStep("bookingDetails");
  };

  // --- Booking Handlers ---
  const handleSlotSelected = (slot: SlotTimeItem, date: string) => {
    setSelectedSlot(slot);
    setSelectedDate(date);
  };

  const formattedDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleBookAppointment = () => {
    const errors: string[] = [];
    if (!patientConcerns.trim()) errors.push("Patient concerns");
    if (!selectedSlot) errors.push("Time slot");
    if (!selectedDoctor) errors.push("Doctor selection");
    if (!selectedClinicSetting) errors.push("Clinic selection");
    if (!patientDetails?.id) errors.push("Patient verification");

    if (errors.length > 0) {
      toast.error(`Please fill: ${errors.join(", ")}`);
      return;
    }

    setShowPaymentModal(true);
  };

  const handleConfirmPaymentAndBook = async (paymentData: PaymentFlowData) => {
    const isOnline = selectedClinicSetting?.clinicId.startsWith("ONL-");

    const appointmentData: AppointmentData = {
      clinicId:
        user?.role?.toLocaleLowerCase() === "clinic" ? user?.id || null : null,
      campId:
        user?.role?.toLocaleLowerCase() === "clinic"
          ? null
          : user?.id || null,
      doctorId: selectedDoctor?.id || "",
      patientId: selectedFamilyMember?.id || patientDetails?.id || "",
      slotId: selectedSlot?.id,
      appointmentDate: selectedDate ?? formattedDate(),
      startTime: selectedSlot?.start_time || "",
      endTime: selectedSlot?.end_time || "",
      patientConcern: patientConcerns,
      consultationType: isOnline ? "Online" : "Offline",
      followUpByPatient: false,
      amount: selectedClinicSetting?.fees || 0,
      appointmentSettingClinicId: selectedClinicSetting?.clinicId || null,
      appointmentBy: "clinic",
      paymentType: paymentData.paymentType,
      isAdvance: paymentData.isAdvance,
      isSplit: paymentData.isSplit,
      splitPayments: paymentData.splitPayments,
    };

    setIsBookingLoading(true);
    try {
      const response =
        await AppointmentService.createAppointment(appointmentData);

      if (response.success) {
        // Firebase for online consultations
        if (isOnline) {
          const docRef = doc(
            collection(db, "patientAvailability"),
            response.data.id
          );
          await setDoc(
            docRef,
            {
              patientId:
                selectedFamilyMember?.id || patientDetails?.id,
              doctorId: selectedDoctor?.id || "",
              clinicId:
                user?.role?.toLocaleLowerCase() === "clinic"
                  ? user?.id || null
                  : null,
              appointmentId: response.data.id,
              age: patientDetails?.age,
              appointmentTime: `${selectedSlot?.start_time} - ${selectedSlot?.end_time}`,
              appointmentDate: selectedDate ?? formattedDate(),
              callStatus: "pending",
              status: "Booked",
              roomStatus: "IDLE",
              gender: patientDetails?.gender || null,
              hospital: "",
              image: selectedDoctor?.profileImage || null,
              name: patientDetails?.name || null,
              mobilenumber: null,
              doctorname: selectedDoctor?.name || null,
              education: null,
              specialization: selectedDoctor?.specialization || null,
              patientprofile: patientDetails?.profileImage || null,
              uid: null,
              channelName: null,
              is_Ia: false,
            },
            { merge: true }
          );
        }

        toast.success("Appointment Booked Successfully");
        setShowPaymentModal(false);
        onClose();
      } else {
        toast.error(response.message || "Failed to book appointment");
      }
    } catch (error) {
      toast.error(
        (error as Error)?.message || "Failed to book appointment"
      );
    } finally {
      setIsBookingLoading(false);
      loadAppointments();
      loadAppointmentCount();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white">
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900">
          Create New Appointment
        </h2>
      </div>

      {/* Stepper */}
      <div className="px-6 py-4 bg-white border-b border-gray-100">
        <div className="flex items-center justify-start gap-0">
          {STEPS.map((step, index) => {
            const isActive = step.key === currentStep;
            const isCompleted = index < stepIndex;

            return (
              <div key={step.key} className="flex items-center">
                {/* Step Circle + Label */}
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      isActive
                        ? "bg-emerald-600 text-white shadow-md shadow-emerald-200"
                        : isCompleted
                        ? "bg-emerald-600 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {step.number}
                  </div>
                  <span
                    className={`text-sm font-medium transition-colors ${
                      isActive
                        ? "text-emerald-700"
                        : isCompleted
                        ? "text-emerald-600"
                        : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector Line */}
                {index < STEPS.length - 1 && (
                  <div
                    className={`w-16 sm:w-24 h-0.5 mx-3 transition-colors ${
                      index < stepIndex ? "bg-emerald-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50/50">
        {currentStep === "patient" && (
          <StepPatient
            mobileNumber={mobileNumber}
            onMobileNumberChange={setMobileNumber}
            isVerified={isVerified}
            isVerifying={isVerifying}
            onVerify={handleVerify}
            patientDetails={patientDetails}
            setPatientDetails={setPatientDetails}
            previouslyConsultedDoctors={previouslyConsultedDoctors}
            previousFamilyDetails={previousFamilyDetails}
            selectedFamilyMember={selectedFamilyMember}
            setSelectedFamilyMember={setSelectedFamilyMember}
            onAddFamilyMember={() => setShowAddFamilyModal(true)}
          />
        )}

        {currentStep === "findDoctor" && (
          <StepFindDoctor onDoctorSelected={handleDoctorSelected} />
        )}

        {currentStep === "bookingDetails" &&
          selectedDoctor &&
          selectedClinicSetting && (
            <StepBookingDetails
              doctor={selectedDoctor}
              clinicSetting={selectedClinicSetting}
              patientDetails={patientDetails}
              selectedFamilyMember={selectedFamilyMember}
              patientConcerns={patientConcerns}
              onPatientConcernsChange={setPatientConcerns}
              onChangeDoctor={() => setCurrentStep("findDoctor")}
              onChangePatient={() => setCurrentStep("patient")}
              onSlotSelected={handleSlotSelected}
              selectedSlot={selectedSlot}
              selectedDate={selectedDate}
              onDateSelected={setSelectedDate}
            />
          )}
      </div>

      {/* Footer Navigation */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
        <div>
          {currentStep !== "patient" && (
            <button
              onClick={handleBack}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
        </div>
        <div>
          {currentStep === "patient" && (
            <button
              onClick={handleContinue}
              disabled={!isVerified}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {currentStep === "bookingDetails" && (
            <button
              onClick={handleBookAppointment}
              disabled={!selectedSlot || !patientConcerns.trim()}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
            >
              Confirm & book appointment
            </button>
          )}
        </div>
      </div>

      {/* Modals */}
      {showNewPatientModal && (
        <div className="absolute inset-0 z-30" style={{ height: "inherit" }}>
          <AddNewPatientModal
            mobileNumber={mobileNumber}
            onClose={() => setShowNewPatientModal(false)}
            onSuccess={(newPatientData) => {
              setShowNewPatientModal(false);
              setIsVerified(true);
              setPatientDetails({
                id: newPatientData.id,
                name: newPatientData.name,
                age: newPatientData.age,
                gender: newPatientData.gender,
                avatar: newPatientData.image
                  ? URL.createObjectURL(newPatientData.image)
                  : "https://randomuser.me/api/portraits/men/1.jpg",
                previousDoctors: [],
              } as any);
            }}
          />
        </div>
      )}

      {showAddFamilyModal && (
        <AddNewFamilyMember
          onClose={() => setShowAddFamilyModal(false)}
          onSuccess={(newFamilyData) => {
            setShowAddFamilyModal(false);
            setPreviousFamilyDetails((prev) => [...prev, newFamilyData]);
          }}
          patientId={patientDetails?.id}
          mobileNumber={mobileNumber}
        />
      )}

      {showPaymentModal && (
        <PaymentSummaryModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          totalAmount={selectedClinicSetting?.fees || 0}
          doctorName={selectedDoctor?.name}
          patientName={
            selectedFamilyMember?.name || patientDetails?.name
          }
          slotTime={`${selectedSlot?.start_time} - ${selectedSlot?.end_time}`}
          date={selectedDate || formattedDate()}
          onConfirmBooking={handleConfirmPaymentAndBook}
          isBookingLoading={isBookingLoading}
        />
      )}
    </div>
  );
}
